import React from 'react';
import Select, { OptionProps } from 'react-select';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useListQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { customSearchStyles } from './searchStyles';
import { Tab } from 'store/sales-invoice/medicineSearch.reducer';

interface Inventory {
  shopId: string;
  shopItemId: string;
  shopItemName: string;
  location: string;
  shelfLevel: number;
  quantity: number;
  sellPrice: number;
  isActive: boolean;
  deletedAt: boolean;
  updatedAt: string;
  nearestExpiryDate?: string | null;
}

interface Shortcut {
  label: string;
  onClick: () => void;
}

interface Props {
  tab: Tab;
  dropdownOpen: boolean;
  setDropdownOpen: (open: boolean) => void;
  isUrdu: boolean;
  onInputChange: (tabId: string, value: string) => void;
  onAddMedicine: (tabId: string, medicine: any) => void;
  shortcuts?: Shortcut[];
  showShortcuts: boolean;
}

/**
 * Custom option UI for inventory items
 */
const CustomOption = (props: OptionProps<any, false>) => {
  const { data, innerRef, innerProps, isFocused } = props;
  const inv: Inventory = data.data;

  const outOfStock = !inv.quantity || inv.quantity <= 0;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`d-flex justify-content-between align-items-center px-3 py-2 ${
        isFocused ? 'bg-primary bg-opacity-25' : ''
      }`}
      style={{ cursor: 'pointer' }}
    >
      {/* Item Name */}
      <div style={{ flex: 2, fontWeight: 600 }}>{inv.shopItemName}</div>

      {/* Location */}
      <div style={{ flex: 1 }}>{inv.location}</div>

      {/* Shelf */}
      <div style={{ flex: 1 }}>Shelf {inv.shelfLevel}</div>

      {/* Price */}
      <div style={{ flex: 1 }}>{inv.sellPrice} PKR</div>

      {/* Quantity */}
      <div
        style={{
          flex: 1,
          color: outOfStock ? 'red' : 'inherit',
          fontWeight: outOfStock ? 700 : 400,
        }}
      >
        {outOfStock ? 'Out of stock' : inv.quantity}
      </div>

      {/* Expiry */}
      <div style={{ flex: 1 }}>{inv.nearestExpiryDate ?? '—'}</div>
    </div>
  );
};

const MedicineSearchInput: React.FC<Props> = ({
  tab,
  isUrdu,
  onInputChange,
  onAddMedicine,
  showShortcuts,
}) => {
  const { t } = useTranslation();

  /** Fetch inventory list */
  const { data: inventoryData } = useListQuery<Inventory>(
    API_ENDPOINTS.TENANT.INVENTORIES.SHOP_BASE,
    { shopId: '293ed466-699e-4618-880f-0e04e7ab1f23' },
  );

  /** Convert inventory to select options */
  const filteredOptions = (inventoryData || []).map((inv) => ({
    label: inv.shopItemName,
    value: inv.shopItemId,
    data: inv,
    isDisabled: inv.quantity === 0 || inv.quantity == null,
  }));

  const handleInputChange = (inputValue: string) => {
    onInputChange(tab.id, inputValue);
  };

  const handleChange = (selected: any) => {
    if (selected) {
      onAddMedicine(tab.id, selected.data); // pass back full inventory row
    }
  };

  const noOptionsMessage = () => t('No results found');

  return (
    <div className="text-center mb-4">
      <Row className="justify-content-center">
        <Col md="8">
          <Select
            options={filteredOptions}
            onInputChange={handleInputChange}
            onChange={handleChange}
            value={null}
            placeholder={t('Type here to search items...')}
            styles={customSearchStyles(isUrdu)}
            noOptionsMessage={noOptionsMessage}
            isClearable
            isSearchable
            menuPlacement="auto"
            openMenuOnClick={false}
            components={{ Option: CustomOption }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default MedicineSearchInput;
